import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader, Table, Select } from '../components/ui';
import { api } from '../lib/api';

interface CronRun {
  id: string;
  name: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  status: 'running' | 'success' | 'failure';
  summary: string;
  data: unknown;
  error: string | null;
}

function relTime(iso: string) {
  const sec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `hace ${sec}s`;
  if (sec < 3600) return `hace ${Math.round(sec / 60)}min`;
  if (sec < 86400) return `hace ${Math.round(sec / 3600)}h`;
  return new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function fmtDur(ms: number | null) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function StatusPill({ s }: { s: CronRun['status'] }) {
  const palette =
    s === 'success'
      ? { bg: 'color-mix(in oklab, var(--accent) 12%, transparent)', fg: 'var(--accent)' }
      : s === 'failure'
      ? { bg: 'color-mix(in oklab, var(--danger) 14%, transparent)', fg: 'var(--danger)' }
      : { bg: 'var(--surface-2)', fg: 'var(--text-2)' };
  return (
    <span
      className="mono"
      style={{
        display: 'inline-block',
        padding: '3px 9px',
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      {s}
    </span>
  );
}

export default function CronRunsPage() {
  const [sp, setSp] = useSearchParams();
  const name = sp.get('name') ?? '';
  const status = sp.get('status') ?? '';
  const page = parseInt(sp.get('page') ?? '1', 10);

  const { data: namesData } = useQuery<{ names: string[] }>({
    queryKey: ['admin', 'cron-runs', 'names'],
    queryFn: () => api('/api/admin/cron-runs/names'),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, refetch, isFetching } = useQuery<{
    items: CronRun[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['admin', 'cron-runs', name, status, page],
    queryFn: () =>
      api('/api/admin/cron-runs', {
        query: { name: name || undefined, status: status || undefined, page },
      }),
    refetchInterval: 30_000, // auto-refresh — these are crons; data moves on its own
  });

  const setFilter = (next: Partial<{ name: string; status: string; page: string }>) => {
    const merged: Record<string, string> = {};
    const allowed = ['name', 'status', 'page'] as const;
    for (const k of allowed) {
      const v = next[k] !== undefined ? next[k] : sp.get(k) ?? '';
      if (v) merged[k] = v as string;
    }
    setSp(merged);
  };

  const nameOptions = [{ value: '', label: 'Todos' }, ...(namesData?.names ?? []).map((n) => ({ value: n, label: n }))];
  const statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'success', label: 'Success' },
    { value: 'failure', label: 'Failure' },
    { value: 'running', label: 'Running' },
  ];

  return (
    <div>
      <PageHeader
        kicker="OBSERVABILIDAD"
        title="Ejecuciones de crons"
        sub="Cada cron persiste su resultado aquí (con TTL de 90 días). Los runs no-op se omiten."
        action={
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            style={{
              background: 'transparent',
              border: '1px solid var(--border-2)',
              color: 'var(--text-2)',
              padding: '8px 14px',
              borderRadius: 10,
              fontSize: 12,
              cursor: isFetching ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {isFetching ? 'Actualizando…' : 'Refrescar'}
          </button>
        }
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 240 }}>
          <Select<string>
            label="Cron"
            value={name}
            onChange={(v) => setFilter({ name: v, page: '1' })}
            options={nameOptions}
          />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select<string>
            label="Estado"
            value={status}
            onChange={(v) => setFilter({ status: v, page: '1' })}
            options={statusOptions}
          />
        </div>
      </div>

      <Table<CronRun>
        columns={[
          {
            key: 'name',
            label: 'Cron',
            render: (r) => (
              <Link
                to={`/cron-runs/${r.id}`}
                className="mono"
                style={{ color: 'var(--text)', textDecoration: 'none', fontWeight: 600, fontSize: 12 }}
              >
                {r.name}
              </Link>
            ),
          },
          { key: 'status', label: 'Estado', render: (r) => <StatusPill s={r.status} />, width: 110 },
          { key: 'summary', label: 'Resumen', render: (r) => <span style={{ color: 'var(--text-2)' }}>{r.summary || '—'}</span> },
          {
            key: 'duration',
            label: 'Duración',
            render: (r) => <span className="mono" style={{ color: 'var(--text-3)', fontSize: 12 }}>{fmtDur(r.durationMs)}</span>,
            width: 90,
          },
          {
            key: 'started',
            label: 'Inicio',
            render: (r) => (
              <span className="mono" style={{ color: 'var(--text-3)', fontSize: 12 }} title={new Date(r.startedAt).toLocaleString('es-MX')}>
                {relTime(r.startedAt)}
              </span>
            ),
            width: 140,
          },
        ]}
        rows={isLoading ? [] : data?.items ?? []}
        onRowClick={(r) => {
          window.location.href = `/cron-runs/${r.id}`;
        }}
        empty={isLoading ? 'Cargando…' : 'Sin ejecuciones registradas en este filtro.'}
      />

      {data && data.total > data.limit && (
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {data.total} ejecuciones · página {data.page} de {Math.ceil(data.total / data.limit)}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              disabled={page <= 1}
              onClick={() => setFilter({ page: String(page - 1) })}
              style={pagerBtn(page <= 1)}
            >
              ←
            </button>
            <button
              disabled={page >= Math.ceil(data.total / data.limit)}
              onClick={() => setFilter({ page: String(page + 1) })}
              style={pagerBtn(page >= Math.ceil(data.total / data.limit))}
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function pagerBtn(disabled: boolean): React.CSSProperties {
  return {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    color: disabled ? 'var(--text-3)' : 'var(--text)',
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
  };
}
